import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Button,
  useDisclosure,
  Box,
  Text,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  InputLeftElement,
  Flex,
  Icon,
  Alert,
  AlertIcon,
  Spinner,
  useToast,
} from "@chakra-ui/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaUser, FaLock, FaEye, FaEyeSlash, FaGavel } from "react-icons/fa";
import { Authcontext } from "../Context/Authcontext";
import { CartContext } from "../Context/CartContext";
import { apiUrl } from "../ApiUrl/ApiUrl";

export function Login({ isStandalone: propIsStandalone }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const isStandalone = propIsStandalone !== undefined ? propIsStandalone : location.pathname === "/login";

  const [inputValues, setInputValues] = useState({ username: "", password: "" });
  const [error, setError] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { correct, setCorrect, login } = useContext(Authcontext);
  const { getData } = useContext(CartContext);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputValues((prev) => ({ ...prev, [name]: value }));
    setError((prev) => ({ ...prev, [name]: "" }));
    setErrorMessage("");
  };

  const validate = (values) => {
    const errors = {};
    if (!values.username.trim()) {
      errors.username = "Email or Mobile Number is required";
    }
    if (!values.password) {
      errors.password = "Password is required";
    } else if (values.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }
    return errors;
  };

  const handleLoginSubmit = async (e) => {
    if (e) e.preventDefault();
    const validationErrors = validate(inputValues);
    if (Object.keys(validationErrors).length > 0) {
      setError(validationErrors);
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const res = await fetch(`${apiUrl}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(inputValues),
      });

      if (!res.ok) {
        throw new Error("Invalid username or password");
      }

      const data = await res.json();
      const { body } = data;

      if (body && body.uid && body.name && body.token) {
        const safeUid = String(body.uid);
        if (login) {
          login({
            token: String(body.token),
            uid: safeUid,
            name: body.name,
            role: body.role || "USER",
            username: body.uname || inputValues.username,
          });
        } else {
          setCorrect(true);
          localStorage.setItem("loginCheck", "true");
          localStorage.setItem("loginsetName", body.name);
          localStorage.setItem("token", String(body.token));
          localStorage.setItem("uid", safeUid);
          if (body.role) localStorage.setItem("role", body.role);
        }

        document.cookie = `username=${encodeURIComponent(body.uname || inputValues.username)}; path=/; max-age=${7 * 24 * 60 * 60}`;

        toast({
          title: "Login Successful",
          description: `Welcome back, ${body.name}!`,
          status: "success",
          duration: 2500,
          isClosable: true,
          position: "top",
        });

        onClose();

        if (body.role === "ADMIN" || body.role === "staff") {
          navigate("/admin");
        } else {
          navigate("/");
        }
      } else {
        setErrorMessage("Login failed. Please verify your credentials.");
      }
    } catch (err) {
      setErrorMessage(err.message || "Bad credentials. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Reusable Form Body
  const renderLoginForm = () => (
    <Box>
      {/* Brand Header */}
      <Flex direction="column" align="center" textAlign="center" mb="6">
        <Box p="3" bg="yellow.500" rounded="xl" color="gray.950" mb="3" shadow="md">
          <Icon as={FaGavel} boxSize="24px" />
        </Box>
        <Text fontSize="22px" fontWeight="800" color="gray.900" letterSpacing="-0.3px">
          Welcome to Navneet Law Studios
        </Text>
        <Text fontSize="13px" color="gray.500" mt="1">
          Sign in to access your orders, law publications & software
        </Text>
      </Flex>

      {errorMessage && (
        <Alert status="error" rounded="lg" mb="4" fontSize="13px" py="2.5">
          <AlertIcon />
          {errorMessage}
        </Alert>
      )}

      <form onSubmit={handleLoginSubmit}>
        {/* Username/Email Input */}
        <FormControl isInvalid={!!error.username} mb="4">
          <FormLabel fontSize="13px" fontWeight="600" color="gray.700">
            Email or Mobile Number
          </FormLabel>
          <InputGroup size="md">
            <InputLeftElement pointerEvents="none">
              <Icon as={FaUser} color="gray.400" />
            </InputLeftElement>
            <Input
              type="text"
              name="username"
              value={inputValues.username}
              onChange={handleChange}
              placeholder="e.g. advocate@example.com or 9876543210"
              rounded="lg"
              fontSize="14px"
              bg="gray.50"
              _focus={{ bg: "white", borderColor: "yellow.500", boxShadow: "0 0 0 1px #EAB308" }}
            />
          </InputGroup>
          {error.username && (
            <Text color="red.500" fontSize="11px" mt="1">
              {error.username}
            </Text>
          )}
        </FormControl>

        {/* Password Input with show/hide toggle */}
        <FormControl isInvalid={!!error.password} mb="2">
          <Flex justify="space-between" align="center">
            <FormLabel fontSize="13px" fontWeight="600" color="gray.700" mb="1">
              Password
            </FormLabel>
            <Link to="/forgotpassword" onClick={onClose}>
              <Text fontSize="12px" fontWeight="600" color="#2563EB" _hover={{ textDecoration: "underline" }}>
                Forgot Password?
              </Text>
            </Link>
          </Flex>
          <InputGroup size="md">
            <InputLeftElement pointerEvents="none">
              <Icon as={FaLock} color="gray.400" />
            </InputLeftElement>
            <Input
              type={showPassword ? "text" : "password"}
              name="password"
              value={inputValues.password}
              onChange={handleChange}
              placeholder="Enter your password"
              rounded="lg"
              fontSize="14px"
              bg="gray.50"
              _focus={{ bg: "white", borderColor: "yellow.500", boxShadow: "0 0 0 1px #EAB308" }}
            />
            <InputRightElement>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPassword(!showPassword)}
                color="gray.500"
                _hover={{ color: "gray.700" }}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <Icon as={showPassword ? FaEyeSlash : FaEye} />
              </Button>
            </InputRightElement>
          </InputGroup>
          {error.password && (
            <Text color="red.500" fontSize="11px" mt="1">
              {error.password}
            </Text>
          )}
        </FormControl>

        {/* Submit CTA */}
        <Button
          type="submit"
          w="100%"
          mt="5"
          size="lg"
          bg="#0F172A"
          color="white"
          rounded="lg"
          fontSize="14.5px"
          fontWeight="700"
          _hover={{ bg: "#1E293B", transform: "translateY(-1px)", shadow: "md" }}
          _active={{ transform: "translateY(0)" }}
          isLoading={isLoading}
          loadingText="Authenticating..."
        >
          Sign In
        </Button>
      </form>

      {/* Switch to Signup */}
      <Flex justify="center" align="center" gap="1" mt="6" pt="4" borderTop="1px solid #F1F5F9" fontSize="13px">
        <Text color="gray.600">New to Navneet Law Studios?</Text>
        <Link to="/signup" onClick={onClose}>
          <Text fontWeight="700" color="#2563EB" _hover={{ textDecoration: "underline" }}>
            Create an Account
          </Text>
        </Link>
      </Flex>
    </Box>
  );

  // If visited via /login URL, show full-page centered layout
  if (isStandalone) {
    return (
      <Box minH="85vh" bg="linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 100%)" py={{ base: "8", md: "16" }} px="4">
        <Box
          maxW="440px"
          mx="auto"
          bg="white"
          p={{ base: "6", md: "8" }}
          rounded="2xl"
          shadow="0 20px 40px -8px rgba(15, 23, 42, 0.12)"
          border="1px solid #E2E8F0"
          className="animate-fade-in"
        >
          {renderLoginForm()}
        </Box>
      </Box>
    );
  }

  // Trigger button + Modal for embedded use
  return (
    <>
      <Button
        onClick={onOpen}
        variant="ghost"
        h="auto"
        py="2"
        px={{ base: "3", md: "4" }}
        rounded="full"
        bg="yellow.500"
        color="gray.950"
        fontSize="13px"
        fontWeight="700"
        _hover={{ bg: "yellow.400", transform: "translateY(-1px)", shadow: "0 4px 12px rgba(234, 179, 8, 0.35)" }}
        _active={{ transform: "translateY(0)" }}
        transition="all 0.2s"
      >
        Sign In
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md" motionPreset="scale">
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent rounded="2xl" p={{ base: "4", md: "6" }} shadow="2xl" border="1px solid rgba(255,255,255,0.1)">
          <ModalCloseButton rounded="full" mt="2" mr="2" />
          <ModalBody p="2">{renderLoginForm()}</ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
export default Login;
